import type { DiaryEntry } from "./entries.js";

export type ScheduleChange =
  | { kind: "moved"; appointmentExternalId: number; startsAt: string }
  | { kind: "cancelled"; appointmentExternalId: number };

export type PatchedDiaryEntry = DiaryEntry & { syncing?: boolean };

function isCancelled(entry: DiaryEntry): boolean {
  if (entry.anonymous) return false;
  return [entry.status, entry.bookingStatus].some((status) =>
    ["cancelled", "canceled"].includes(String(status ?? "").toLowerCase()),
  );
}

/** Apply only confirmed, named appointment changes until the mirror catches up. */
export function applyPatches(
  entries: DiaryEntry[],
  patches: ReadonlyMap<number, ScheduleChange>,
): PatchedDiaryEntry[] {
  const patched: PatchedDiaryEntry[] = [];

  for (const entry of entries) {
    if (entry.anonymous || entry.externalId === null) {
      patched.push(entry);
      continue;
    }

    const patch = patches.get(entry.externalId);
    if (patch === undefined) {
      patched.push(entry);
      continue;
    }
    if (patch.kind === "cancelled") continue;

    const start = Date.parse(entry.startsAt);
    const end = Date.parse(entry.endsAt);
    const nextStart = Date.parse(patch.startsAt);
    const duration = end - start;
    patched.push({
      ...entry,
      startsAt: patch.startsAt,
      endsAt: Number.isFinite(nextStart) && Number.isFinite(duration)
        ? new Date(nextStart + duration).toISOString()
        : entry.endsAt,
      syncing: true,
    });
  }

  return patched.sort((left, right) => left.startsAt.localeCompare(right.startsAt));
}

/** Whether one authoritative read now reflects a schedule mutation. */
export function confirmedBy(entries: DiaryEntry[], patch: ScheduleChange): boolean {
  const matches = entries.filter(
    (entry) => !entry.anonymous && entry.externalId === patch.appointmentExternalId,
  );
  if (patch.kind === "moved") {
    // Compare instants, not strings: the read may spell the same moment with
    // a different offset or precision than the patch.
    return matches.some((entry) => Date.parse(entry.startsAt) === Date.parse(patch.startsAt));
  }
  return matches.length === 0 || matches.every(isCancelled);
}
